import React, { useState, useEffect, useMemo, memo } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCheck, Users, X, Hand } from 'lucide-react';
import toast from 'react-hot-toast';
import { projectAPI, chatAPI, createQueryKey } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import GoalsModal from './GoalsModal';
import ParticipantsModal from './ParticipantsModal';
import QuickShoutModal from './QuickShoutModal';

const ProjectStatusBar = memo(({ project, onFinish, isReadOnly = false, allowPointerPassthrough = false }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isGoalsModalOpen, setIsGoalsModalOpen] = useState(false);
  const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);
  const [isQuickShoutModalOpen, setIsQuickShoutModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Check if current user is the project owner
  const isOwner = user && project && user.id === project.ownerId;

  // Handle window resize for responsive time format
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate objectives progress for Goals button visualization (simplified model)
  const objectivesProgress = useMemo(() => {
    if (!project?.objectives || !Array.isArray(project.objectives)) {
      return { completed: 0, total: 0, hasProgress: false, percentage: 0, progressText: "0/0" };
    }
    
    const objectives = project.objectives;
    const total = objectives.length;
    
    // Simplified model: just count objectives where isCompleted === true
    const completedObjectives = objectives.filter(obj => obj.isCompleted === true).length;
    
    return {
      completed: completedObjectives,
      total,
      hasProgress: total > 0,
      percentage: total > 0 ? Math.round((completedObjectives / total) * 100) : 0,
      progressText: `${completedObjectives}/${total}`
    };
  }, [project?.objectives]);

  // Check if this is a Site project (defined early so we can use it for queries)
  const isSiteProject = project?.settings?.archetype === 'site';
  const isEndlessProject = project?.gameMode === 'endless';
  
  // Fetch chats for Site projects to calculate accurate participant count
  // No polling needed - WebSocket events (participant_joined/participant_left) invalidate this query
  const { data: chatsResponse } = useQuery({
    queryKey: createQueryKey.chats(project?.id),
    queryFn: () => chatAPI.getByProject(project.id),
    enabled: !!project?.id && isSiteProject && project?.isMultiplayer,
    staleTime: 0, // Always consider stale to ensure refetch on invalidation
    refetchOnMount: true, // Refetch when component mounts
  });

  // Fetch active WebSocket users (users currently online)
  // WebSocket events provide primary updates, but we also poll periodically as a fallback
  const { data: activeWebSocketUsersResponse } = useQuery({
    queryKey: ['active-websocket-users', project?.id],
    queryFn: () => projectAPI.getActiveWebSocketUsers(project.id),
    enabled: !!project?.id && project?.isMultiplayer,
    staleTime: 0, // Always consider stale to ensure refetch on invalidation
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus (WebSocket events handle this)
    refetchInterval: 15000, // Poll every 15 seconds as fallback (WebSocket events are primary)
  });
  
  // Calculate participant count - only count users with active chats AND active WebSocket connections
  const participantsCount = useMemo(() => {
    if (!project?.isMultiplayer) return 0;
    
    // Get active WebSocket user IDs (users currently online)
    const activeWebSocketUserIds = new Set(
      activeWebSocketUsersResponse?.data?.data?.activeUserIds || []
    );
    
    // For Site projects: count users with active chats AND active WebSocket connections
    if (isSiteProject) {
      if (!chatsResponse || !activeWebSocketUsersResponse) {
        // While loading, use backend count as fallback
        return project?.participantCount ?? 0;
      }
      
      const chats = chatsResponse?.data?.data?.chats || [];
      const activeChatUserIds = new Set(
        chats
          .filter(c => c.isActive === true && !c.archivedAt)
          .map(c => c.userId)
          .filter(Boolean)
      );
      
      // Intersection: users with both active chats AND active WebSocket connections
      const activeParticipantIds = new Set(
        [...activeChatUserIds].filter(id => activeWebSocketUserIds.has(id))
      );
      
      // Always include the project owner even if they don't have an active WebSocket connection
      if (project?.ownerId) {
        activeParticipantIds.add(project.ownerId);
      }
      
      return activeParticipantIds.size;
    }
    
    // For Event projects: count users with active WebSocket connections
    // (they should have active collaborations, but we verify they're online)
    if (activeWebSocketUsersResponse) {
      // Always include the project owner
      if (project?.ownerId) {
        activeWebSocketUserIds.add(project.ownerId);
      }
      return activeWebSocketUserIds.size;
    }
    
    // Fallback: use backend participantCount while loading
    return project?.participantCount ?? 0;
  }, [project?.participantCount, project?.ownerId, project?.isMultiplayer, isSiteProject, chatsResponse, activeWebSocketUsersResponse]);

  // Update project status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ status }) => {
      return projectAPI.updateStatus(project.id, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(createQueryKey.project(project.id));
      queryClient.invalidateQueries(['user-projects']); // Invalidate ProjectsPage queries
      queryClient.invalidateQueries(['homepage-games']); // Refresh homepage games list
    },
    onError: (error) => {
      console.error('❌ Status update failed:', error);
      toast.error('Failed to update project status');
    }
  });

  // Simple server-synced timer (like waiting room) 🎯
  useEffect(() => {
    if (!project) return;

    // Optimization: Skip timer for Site and Endless projects as we don't display it
    if (isSiteProject || isEndlessProject) {
      return;
    }

    // For finished projects, show duration between scheduledStartAt and finishedAt
    if (project.status === 'finished') {
      if (project.scheduledStartAt && project.finishedAt) {
        const start = new Date(project.scheduledStartAt);
        const end = new Date(project.finishedAt);
        const totalSeconds = Math.floor((end - start) / 1000);
        setElapsedSeconds(Math.max(0, totalSeconds));
      } else {
        // Fallback: if missing timestamps, show 0
        setElapsedSeconds(0);
      }
      return; // Static display, no interval needed
    }

    // For ongoing projects, calculate time since scheduledStartAt
    if (project.status === 'ongoing') {
      if (!project.scheduledStartAt) {
        // Defensive: If no scheduledStartAt, use createdAt as fallback
        // Note: Site projects don't have scheduledStartAt, so this is expected
        const projectArchetype = project.settings?.archetype;
        if (projectArchetype !== 'site') {
          console.warn('⚠️ Ongoing project missing scheduledStartAt, using createdAt fallback');
        }
        const fallbackStart = project.createdAt ? new Date(project.createdAt) : new Date();
        const now = new Date();
        const elapsed = Math.floor((now - fallbackStart) / 1000);
        setElapsedSeconds(Math.max(0, elapsed));
        
        const interval = setInterval(() => {
          const now = new Date();
          const elapsed = Math.floor((now - fallbackStart) / 1000);
          setElapsedSeconds(Math.max(0, elapsed));
        }, 1000);
        return () => clearInterval(interval);
      }

      const calculateElapsed = () => {
        const start = new Date(project.scheduledStartAt);
        const now = new Date();
        const elapsed = Math.floor((now - start) / 1000);
        setElapsedSeconds(Math.max(0, elapsed)); // Never negative
      };

      // Update immediately
      calculateElapsed();

      // Then update every second
      const interval = setInterval(calculateElapsed, 1000);
      return () => clearInterval(interval);
    }

    // For any other state (pending, waiting, paused), timer is 0
    setElapsedSeconds(0);
  }, [project, isSiteProject, isEndlessProject]);

  // Calculate total duration and progress (for scheduled quests)
  const scheduledDuration = useMemo(() => {
    if (!project?.scheduledStartAt || !project?.scheduledEndAt) return null;
    const start = new Date(project.scheduledStartAt);
    const end = new Date(project.scheduledEndAt);
    const totalSeconds = Math.floor((end - start) / 1000);
    return totalSeconds > 0 ? totalSeconds : null;
  }, [project?.scheduledStartAt, project?.scheduledEndAt]);

  const progress = useMemo(() => {
    if (!scheduledDuration) return null;
    const percentage = Math.min(100, Math.max(0, (elapsedSeconds / scheduledDuration) * 100));
    const isOvertime = elapsedSeconds > scheduledDuration;
    const remainingSeconds = Math.max(0, scheduledDuration - elapsedSeconds);
    return { percentage, isOvertime, remainingSeconds };
  }, [scheduledDuration, elapsedSeconds]);

  // Format time display with mobile-optimized compact format
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      // Mobile: use colon format (3:19:40) - much more compact
      // Desktop: keep full format (3h 19m 40s) for clarity
      return isMobile 
        ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        : `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      // Mobile: use colon format (19:40) 
      // Desktop: keep full format (19m 40s)
      return isMobile
        ? `${minutes}:${secs.toString().padStart(2, '0')}`
        : `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Compact time format (for progress display) - always compact
  const formatTimeCompact = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      // Always use colon format for compact display
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}m`;
    }
  };

  // Leave project mutation (for non-owners)
  const leaveProjectMutation = useMutation({
    mutationFn: () => projectAPI.leave(project.id),
    onSuccess: () => {
      toast.success('Left project');
      queryClient.invalidateQueries(createQueryKey.project(project.id));
      queryClient.invalidateQueries(['participants', project.id]);
      queryClient.invalidateQueries(['project-chats', project.id]); // Invalidate chats (active chat was archived)
      queryClient.invalidateQueries(['chats', project.id]); // Invalidate chat queries
      // Navigate to explore page
      setTimeout(() => {
        window.location.href = '/starters';
      }, 500);
    },
    onError: (error) => {
      console.error('❌ Leave project failed:', error);
      toast.error('Failed to leave project');
    }
  });

  const navigate = useNavigate();

  // Archive session mutation (for Site projects)
  const archiveSessionMutation = useMutation({
    mutationFn: () => chatAPI.archiveCurrentSession(project.id),
    onSuccess: async () => {
      toast.success('Session ended');
      
      // Immediately update the cache to mark the active chat as archived
      // This provides instant UI feedback before refetch
      queryClient.setQueryData(['project-chats', project.id], (oldData) => {
        if (!oldData?.data?.data?.chats) return oldData;
        
        const updatedChats = oldData.data.data.chats.map(chat => {
          // Mark the active chat as archived immediately
          if (chat.isActive === true && chat.userId === user?.id) {
            return {
              ...chat,
              isActive: false,
              archivedAt: new Date().toISOString()
            };
          }
          return chat;
        });
        
        return {
          ...oldData,
          data: {
            ...oldData.data,
            data: {
              ...oldData.data.data,
              chats: updatedChats
            }
          }
        };
      });
      
      // Invalidate all related queries
      queryClient.invalidateQueries(['project-chats', project.id]);
      queryClient.invalidateQueries(['chats', project.id]);
      queryClient.invalidateQueries(createQueryKey.chats(project.id));
      queryClient.invalidateQueries(createQueryKey.project(project.id));
      
      // Force refetch ALL matching queries to ensure fresh data
      try {
        await queryClient.refetchQueries({
          queryKey: ['project-chats', project.id],
          type: 'all'
        });
      } catch (err) {
        console.warn('Refetch error (non-critical):', err);
      }
      
      // Navigate back to dashboard
      setTimeout(() => {
        navigate(`/projects/${project.id}`, { state: { sessionEnded: true } });
      }, 200);
    },
    onError: (error) => {
      console.error('Failed to archive session:', error);
      toast.error('Failed to end session');
    }
  });

  // Handle finish (with optional parent override for confirmation)
  const handleFinish = () => {
    // For Site projects: archive the current session instead of finishing the project
    if (isSiteProject) {
      if (typeof onFinish === 'function') {
        onFinish(); // Allow parent to show confirmation dialog
        return;
      }
      // Direct archive without confirmation
      archiveSessionMutation.mutate();
      return;
    }
    
    // Non-owners should leave, not finish
    if (!isOwner) {
      leaveProjectMutation.mutate();
      return;
    }
    
    if (typeof onFinish === 'function') {
      onFinish(); // Allow parent to show confirmation dialog
      return;
    }
    // Direct finish without confirmation
    updateStatusMutation.mutate({ status: 'finished' });
  };
  
  if (!project || project.status === 'pending' || project.status === 'waiting') {
    return null; // Don't show for pending/waiting projects
  }

  // Don't show "Completed" banner for Site projects - they're ongoing/utilitarian
  // Even if somehow marked as finished, Site projects should show the normal status bar
  if (project.status === 'finished' && !isSiteProject) {
    return (
      <div className="px-3 py-2.5">
        <div 
          className="inline-flex items-center gap-3 px-4 py-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-full"
          style={allowPointerPassthrough ? { pointerEvents: 'none' } : undefined}
        >
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full" />
            <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">Completed</span>
          </div>
          <span className="text-xs text-emerald-600 dark:text-emerald-400 tabular-nums">
            {formatTime(elapsedSeconds)}
            </span>
        </div>
      </div>
    );
  }

  // Shared pill styles for visual consistency
  const pointerEventsClass = allowPointerPassthrough ? ' pointer-events-auto' : '';
  const basePillStyles = `inline-flex items-center gap-1.5 h-11 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.97]${pointerEventsClass}`;
  const defaultPillStyles = `${basePillStyles} bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-200`;
  const dangerPillStyles = `${basePillStyles} bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow hover:border-gray-300 dark:hover:border-gray-600 text-red-600 dark:text-red-400 focus-visible:ring-red-500`;

  // For ongoing or paused (though paused won't be used in UI anymore)
  const barPointerProps = allowPointerPassthrough ? { style: { pointerEvents: 'none' } } : {};

  return (
    <div className="px-2 py-2.5 w-full">
      <div className="flex items-center justify-between gap-2" {...barPointerProps}>
        
        {/* Stop/Leave Project button */}
        <button
          onClick={handleFinish}
          disabled={updateStatusMutation.isLoading || archiveSessionMutation.isLoading}
          className={`${dangerPillStyles} ${isMobile ? 'w-11 justify-center' : 'px-4'} disabled:opacity-50 disabled:cursor-not-allowed`}
          title={
            isSiteProject 
              ? "End current session" 
              : isOwner 
                ? "Finish the project for everyone" 
                : "Leave this project"
          }
        >
          <X className="w-4 h-4" />
          <span className='hidden xl:inline'>
            {isSiteProject 
              ? 'End Session' 
              : isOwner 
                ? 'End' 
                : 'Leave'}
          </span>
        </button>

        {/* Timer display with progress - centered and prominent */}
        <div className="flex-1 flex items-center justify-center">
            {!isSiteProject && !isEndlessProject && (
              scheduledDuration && progress ? (
            // Scheduled project with end time - clean progress display
            <div className={`relative inline-flex items-center gap-2 h-11 ${isMobile ? 'px-3.5' : 'px-4'} bg-white dark:bg-gray-900 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm`}>
              <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-900 dark:text-white font-semibold tabular-nums tracking-tight">
                {formatTimeCompact(elapsedSeconds)}
                <span className="text-gray-400 dark:text-gray-500 font-normal mx-0.5">/</span>
                {formatTimeCompact(scheduledDuration)}
              </span>
                {progress.isOvertime && (
                <span className="text-[10px] text-red-500 font-bold uppercase tracking-wider">OT</span>
                )}
              {/* Progress bar */}
              <div className="absolute left-3 right-3 bottom-1.5 h-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                      progress.isOvertime
                        ? 'bg-red-500'
                        : progress.percentage > 80
                        ? 'bg-amber-500'
                        : 'bg-emerald-500'
                    }`}
                  style={{ width: `${Math.min(progress.percentage, 100)}%` }}
                  />
                </div>
              </div>
            ) : (
              // No end time - simple timer chip
            <div className={`inline-flex items-center gap-1.5 h-11 ${isMobile ? 'px-3.5' : 'px-4'} bg-white dark:bg-gray-900 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm`}>
              <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm text-gray-900 dark:text-white font-semibold tabular-nums tracking-tight">{formatTime(elapsedSeconds)}</span>
              </div>
            ))}
          </div>

        {/* Right side action buttons */}
        <div className="flex items-center gap-1.5">
        {/* Participants button - only show for multiplayer projects and not read-only */}
        {project.isMultiplayer && !isReadOnly && (
          <button
            onClick={() => setIsParticipantsModalOpen(true)}
            className={`${defaultPillStyles} ${isMobile ? (participantsCount > 1 ? 'px-2.5' : 'w-11 justify-center') : 'px-3.5'} focus-visible:ring-blue-500`}
          >
              <Users className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              {participantsCount > 1 ? (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[11px] font-bold">
                  {participantsCount}
                </span>
              ) : (
                <span className="hidden xl:inline text-gray-600 dark:text-gray-300">Invite</span>
              )}
          </button>
        )}

        {/* Quick Shout button - only for multiplayer ongoing and not read-only */}
        {project.isMultiplayer && participantsCount > 1 && project.status === 'ongoing' && !isReadOnly && (
          <button
            onClick={() => setIsQuickShoutModalOpen(true)}
            className={`${defaultPillStyles} ${isMobile ? 'w-11 justify-center' : 'px-3.5'} hover:bg-purple-50 dark:hover:bg-purple-950/30 focus-visible:ring-purple-500`}
            title="Send a quick shout to all participants"
          >
              <Hand className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className='hidden xl:inline text-gray-600 dark:text-gray-300'>Shout</span>
          </button>
        )}

        {/* Goals button with progress visualization - hidden in read-only mode */}
        {!isReadOnly && (
        <button
          onClick={() => setIsGoalsModalOpen(true)}
          className={`${defaultPillStyles} ${isMobile ? (objectivesProgress.hasProgress ? 'px-2.5' : 'w-11 justify-center') : 'px-3.5'} focus-visible:ring-blue-500`}
        >
              <CheckCheck className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              {objectivesProgress.hasProgress ? (
                <span className="inline-flex items-center justify-center min-w-[32px] h-[18px] px-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 text-[11px] font-bold">
              {objectivesProgress.progressText}
            </span>
              ) : (
                <span className='hidden xl:inline text-gray-600 dark:text-gray-300'>Goals</span>
          )}
        </button>
        )}

        </div>
      </div>
      
      {/* Goals Modal */}
      <GoalsModal 
        isOpen={isGoalsModalOpen}
        onClose={() => setIsGoalsModalOpen(false)}
        project={project}
        currentUser={user}
      />

      {/* Participants Modal */}
      <ParticipantsModal
        isOpen={isParticipantsModalOpen}
        onClose={() => setIsParticipantsModalOpen(false)}
        projectId={project?.id}
        project={project}
      />

      {/* Quick Shout Modal */}
      {project?.isMultiplayer && project?.status === 'ongoing' && (
        <QuickShoutModal
          isOpen={isQuickShoutModalOpen}
          projectId={project.id}
          onClose={() => setIsQuickShoutModalOpen(false)}
          onSendShout={async (message) => {
            await projectAPI.broadcastShout(project.id, message);
          }}
        />
      )}
    </div>
  );
});

ProjectStatusBar.displayName = 'ProjectStatusBar';

export default ProjectStatusBar;